/**
 * InspectionsPage - Inspection management page using the shared JobListTemplate.
 *
 * Configures the JobListTemplate with inspection-specific stat cards, columns,
 * filters, and API fetch logic. The layout, pagination, skeleton loading, and
 * filter panel UI are handled entirely by the template.
 *
 * Features:
 *	 - Stat cards: Open Inspections, Avg Resolution, Pending Review, Deficiencies
 *	 - Table columns: Inspection ID, Building & Customer, Status, Inspector, Assignee, Date, Actions
 *	 - Filters: Status (checkboxes), Date Range, Building (text search)
 *
 * Inspections follow their own lifecycle separate from work orders:
 *	 pending -> in_progress -> observations_complete -> corrections_in_progress -> completed
 *
 * Data source: GET /api/inspections with search, filter, and pagination params.
 *
 * Accessible by: all authenticated users.
 * Role-specific: managers/admins/developers can create new inspections.
 *
 * @route /inspections
 */
import React, { useState, useEffect, useMemo } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { useAuth } from '../../hooks/useAuth';
import { useDeleteJob } from '../../hooks/useDeleteJob';
import { inspectionService } from '../../services/inspectionService';
import type { Inspection, PaginatedInspectionResponse } from '../../services/inspectionService';
import { normalizeInspection } from '../../services/inspectionService';
import { camelizeKeys } from '../../services/api';
import { qk } from '../../lib/queryKeys';
import { invalidateJobCaches } from '../../lib/invalidateJobCaches';
import { isoDaysAgo } from '../../utils/formatDate';
import { formatStatusLabel } from '../../utils/formatLabel';
import { usePersistedFilterState } from '../../hooks/usePersistedFilterState';
import { INSPECTION_RATINGS } from '../../utils/inspectionRating';
import { JobListTemplate } from '../../components/templates/JobListTemplate';
import type { StatCardConfig, ColumnConfig, FilterControlConfig } from '../../components/templates/JobListTemplate';
import { ActionMenu, RecurrenceBadge, DateTimeStack } from '../../components/atoms';
import { JobStatusActionButton, JobProgressIndicator } from '../../components/molecules';
import { generateInspectionListPdf } from '../../utils/pdfGenerator';
import type { ListFilterSummary } from '../../utils/pdfGenerator';

/* ============================================
 * FILTER OPTIONS
 * Used by the filter panel via FilterControlConfig.
 * ============================================ */

/** Status options for the checkbox filter group */
const STATUS_FILTER_OPTIONS = [
	{ value: 'pending', label: 'Pending' },
	{ value: 'in_progress', label: 'In Progress' },
	{ value: 'observations_complete', label: 'Observations Complete' },
	{ value: 'corrections_in_progress', label: 'Corrections In Progress' },
	{ value: 'completed', label: 'Completed' },
];

/** Rating options — the shared three-tier vocabulary plus the "Not Rated"
 *  (unrated) state, matching the dashboard Inspection Ratings tiles. */
const RATING_FILTER_OPTIONS = [
	...INSPECTION_RATINGS.map((r) => ({ value: r.value, label: r.label })),
	{ value: 'unrated', label: 'Not Rated' },
];

/* ============================================
 * FILTER DEFAULTS
 * The Filter Control opens pre-narrowed to active inspections (In Progress /
 * Pending) over the last two weeks. A signed-in user can change these and
 * their choices persist in localStorage until they sign out
 * (see usePersistedFilterState).
 * ============================================ */

/** Default status selection — active inspections only. */
const DEFAULT_STATUS_FILTER = ['in_progress', 'pending'];
/** Default date-range start — look back two weeks from today. */
const DEFAULT_DATE_FROM = isoDaysAgo(14);

/* Snake_case → Title Case formatting lives in utils/formatLabel — this
   page used to define an inline formatStatusLabel helper that did the
   same thing. */

/* ============================================
 * MAIN COMPONENT
 * ============================================ */
const InspectionsPage: React.FC = () => {
	const navigate = useNavigate();
	const { hasAnyRole } = useAuth();
	const queryClient = useQueryClient();

	/* ---- Pagination (page is UI state; totals come from the query) ---- */
	const [currentPage, setCurrentPage] = useState(1);

	/* ---- Deep-link filters ----
	 * The dashboard stat cards link here with ?status=…&date_from=…&date_to=….
	 * When any of those params is present, the URL defines the COMPLETE filter
	 * state — unspecified dimensions reset to cleared (not to the persisted
	 * copy or the 2-week default) so the landing list matches the card's
	 * number. The values flow through the persisted-state overrides below,
	 * then the params are stripped from the URL (one-shot semantics). */
	const [searchParams, setSearchParams] = useSearchParams();
	const hasDeepLink = searchParams.has('status') || searchParams.has('date_from') || searchParams.has('date_to') || searchParams.has('rating');
	const deepLink = hasDeepLink
		? {
			statuses: (searchParams.get('status') ?? '').split(',').filter(Boolean),
			dateFrom: searchParams.get('date_from') ?? '',
			dateTo: searchParams.get('date_to') ?? '',
			ratings: (searchParams.get('rating') ?? '').split(',').filter(Boolean),
		}
		: undefined;

	useEffect(() => {
		if (hasDeepLink) setSearchParams({}, { replace: true });
	}, [hasDeepLink, setSearchParams]);

	/* ---- Filter state ----
	 * Persisted to localStorage per-field so a signed-in user's Filter Control
	 * selections survive reloads/navigation; the defaults (active statuses +
	 * 2-week look-back) apply on a fresh session and after sign-out. */
	const [filterStatuses, setFilterStatuses] = usePersistedFilterState<string[]>('inspections.statuses', DEFAULT_STATUS_FILTER, deepLink?.statuses);
	const [filterDateFrom, setFilterDateFrom] = usePersistedFilterState('inspections.dateFrom', DEFAULT_DATE_FROM, deepLink?.dateFrom);
	const [filterDateTo, setFilterDateTo] = usePersistedFilterState('inspections.dateTo', '', deepLink?.dateTo);
	const [filterBuilding, setFilterBuilding] = usePersistedFilterState('inspections.building', '', deepLink ? '' : undefined);
	const [filterRatings, setFilterRatings] = usePersistedFilterState<string[]>('inspections.ratings', [], deepLink?.ratings);

	/* Managers, admins, super_admins, and developers can create new inspections */
	const canCreate = hasAnyRole(['super_admin', 'admin', 'manager', 'developer']);
	/* Row Delete — confirms, soft-deletes and refreshes the list (shared
	   with the detail page via useDeleteJob). */
	const { requestDelete, deleteModal } = useDeleteJob('inspection');

	/* PDF list-export state — flips while jspdf renders the doc so we can
	 * disable the button instead of letting the user double-trigger it. */
	const [isGeneratingPdf, setIsGeneratingPdf] = useState(false);

	/* Build query params from active filters + page. Memoized so the query key
	 * is stable and changing a filter produces a new key TanStack fetches. */
	const params = useMemo<Record<string, string>>(() => {
		const p: Record<string, string> = { page: String(currentPage) };
		if (filterStatuses.length > 0) p.status = filterStatuses.join(',');
		if (filterRatings.length > 0) p.rating = filterRatings.join(',');
		if (filterDateFrom) p.date_from = filterDateFrom;
		if (filterDateTo) p.date_to = filterDateTo;
		if (filterBuilding) p.search = filterBuilding;
		return p;
	}, [currentPage, filterStatuses, filterRatings, filterDateFrom, filterDateTo, filterBuilding]);

	/* ---- Inspections read (TanStack Query) ----
	 * queryFn keeps the both-shapes handling + camelize/normalize the page used
	 * before, returning the list plus pagination meta as one object. */
	const query = useQuery({
		queryKey: qk.inspections.list(params),
		queryFn: async () => {
			const response = await inspectionService.getAll(params);
			const data = response.data as PaginatedInspectionResponse;
			if (data && Array.isArray(data.data)) {
				const items = (camelizeKeys(data.data) as Record<string, unknown>[]).map(normalizeInspection);
				return { items, totalPages: data.last_page || 1, totalItems: data.total || data.data.length, perPage: data.per_page || 25 };
			}
			if (Array.isArray(response.data)) {
				const items = (camelizeKeys(response.data) as Record<string, unknown>[]).map(normalizeInspection);
				return { items, totalPages: 1, totalItems: items.length, perPage: 25 };
			}
			return { items: [] as Inspection[], totalPages: 1, totalItems: 0, perPage: 25 };
		},
	});

	const inspections: Inspection[] = useMemo(() => query.data?.items ?? [], [query.data]);
	const totalPages = query.data?.totalPages ?? 1;
	const totalItems = query.data?.totalItems ?? 0;
	const perPage = query.data?.perPage ?? 25;
	const isLoading = query.isLoading;
	const error = query.error ? (query.error as Error).message : null;

	/* Stat card values derived from the current page's list. */
	const openInspCount = useMemo(() => inspections.filter((insp) => ['pending', 'in_progress'].includes(insp.status)).length, [inspections]);
	const pendingReviewCount = useMemo(() => inspections.filter((insp) => insp.status === 'observations_complete').length, [inspections]);
	const deficienciesCount = useMemo(() => inspections.filter((insp) => insp.status === 'corrections_in_progress').length, [inspections]);

	/* Reset to page 1 when filters change */
	useEffect(() => {
		setCurrentPage(1);
	}, [filterStatuses, filterRatings, filterDateFrom, filterDateTo, filterBuilding]);

	/**
	 * Transition an inspection from 'pending' into 'in_progress'.
	 * Wired up to the row-level Start CTA. After the PATCH succeeds the list
	 * caches are invalidated so the row's status badge + CTA reflect the new
	 * state, and the caller navigates to the detail page to begin the review.
	 * Starting a review also moves it between Dashboard status buckets, so the
	 * Dashboard reads go with it — see invalidateJobCaches.
	 */
	const startInspectionMutation = useMutation({
		mutationFn: (id: string | number) => inspectionService.update(String(id), { status: 'in_progress' }),
		onSuccess: () => {
			invalidateJobCaches(queryClient, 'inspection');
		},
		onError: (err) => {
			console.error('[InspectionsPage] Failed to start inspection:', err);
		},
	});
	const handleStartInspection = (id: string | number) => startInspectionMutation.mutate(id);

	/* ============================================
	 * STAT CARD CONFIGURATION
	 * ============================================ */

	const stats: StatCardConfig[] = [
		{ label: 'OPEN INSPECTIONS', value: openInspCount, subtitle: 'Active this period', isAccent: true },
		{ label: 'AVG RESOLUTION', value: '--', subtitle: 'Days to complete', valueColor: 'text-brand-accent' },
		{ label: 'PENDING REVIEW', value: pendingReviewCount, subtitle: 'Awaiting assessment', valueColor: 'text-yellow-600', subtitleColor: 'text-yellow-500' },
		{ label: 'DEFICIENCIES', value: deficienciesCount, subtitle: 'Corrections in progress', valueColor: 'text-red-600', subtitleColor: 'text-red-500' },
	];

	/* ============================================
	 * TABLE COLUMN CONFIGURATION
	 * ============================================ */

	const columns: ColumnConfig[] = [
		{
			/* Date leads the row — stacked DAY / MM/DD/YY / time (EST) via the
			   shared DateTimeStack atom. */
			key: 'date',
			header: 'Date',
			render: (insp: Inspection) => (
				<DateTimeStack date={insp.inspectionDate} className="text-12px" />
			),
		},
		{
			key: 'id',
			header: 'Inspection ID',
			render: (insp: Inspection) => (
				<button type="button" onClick={() => navigate(`/inspections/${insp.id}`)} className="text-14px font-semibold text-brand-accent hover:underline">INS-{String(insp.id).slice(0, 8)}</button>
			),
		},
		{
			key: 'status',
			header: 'Status',
			/* Completed inspections collapse to a check icon; others
			   render the shared progress bar so the row tells you both
			   "what stage" and "how far along". */
			render: (insp: Inspection) => (
				<JobProgressIndicator status={insp.status} type="inspection" />
			),
		},
		{
			key: 'inspector',
			header: 'Inspector',
			render: (insp: Inspection) => insp.inspectorName ? (
				<div className="flex items-center gap-8px">
					<div className="w-24px h-24px rounded-full bg-brand-accent flex items-center justify-center flex-shrink-0">
						<span className="text-12px font-bold text-white">{insp.inspectorName.charAt(0).toUpperCase()}</span>
					</div>
					<span className="text-12px text-brand-text line-clamp-1">{insp.inspectorName}</span>
				</div>
			) : (
				<span className="text-12px text-brand-text-muted">Unassigned</span>
			),
		},
		{
			key: 'assignee',
			header: 'Assignee',
			render: (insp: Inspection) => insp.assigneeName ? (
				<div className="flex items-center gap-8px">
					<div className="w-24px h-24px rounded-full bg-brand-primary-light flex items-center justify-center flex-shrink-0">
						<span className="text-12px font-bold text-white">{insp.assigneeName.charAt(0).toUpperCase()}</span>
					</div>
					<span className="text-12px text-brand-text line-clamp-1">{insp.assigneeName}</span>
				</div>
			) : (
				<span className="text-12px text-brand-text-muted">Unassigned</span>
			),
		},
		{
			/* Primary status-driven CTA — Start when the inspection
			   is still pending, Resume during the in-flight statuses
			   (in_progress, observations_complete, corrections_in_progress),
			   View once completed/cancelled. Start and an in_progress
			   Resume deep-link into ?workflow=1 so the inspection detail
			   page auto-opens the 3-step InspectionWorkflow overlay
			   (Checklist → Verify → Sign-Off), mirroring the work-order
			   Start/Resume flow. Once observations are complete the overlay
			   has nothing left to do — it would open on the "Inspection
			   Submitted" screen with no next step — so later statuses land
			   on the detail page, whose action bar offers Complete
			   Inspection or Create Follow-Up. */
			key: 'primaryAction',
			header: '',
			render: (insp: Inspection) => (
				<JobStatusActionButton
					status={insp.status}
					ariaLabel={`${insp.status} action for inspection ${insp.id}`}
					onStart={() => handleStartInspection(insp.id)}
					/* insp.status is read at click time: right after Start it is
					   still 'pending' in this row until the list refetches, which
					   is why 'pending' is in the overlay set. */
					onActive={() => navigate(
						insp.status === 'pending' || insp.status === 'in_progress'
							? `/inspections/${insp.id}?workflow=1`
							: `/inspections/${insp.id}`,
					)}
					onView={() => navigate(`/inspections/${insp.id}`)}
				/>
			),
		},
		{
			key: 'actions',
			header: '',
			widthClass: 'w-48px',
			render: (insp: Inspection) => (
				<ActionMenu
					ariaLabel={`Actions for inspection ${insp.id}`}
					items={[
						{ label: 'View', onClick: () => navigate(`/inspections/${insp.id}`) },
						canCreate && { label: 'Edit', onClick: () => navigate(`/inspections/${insp.id}/edit`) },
						/* Duplicate -- opens the create form prefilled from this inspection.
						   Carries job details (company, location, contact, settings,
						   estimate, notes, checklist) but resets dates, status, photos,
						   signatures, and approvals so the new record is a fresh visit. */
						canCreate && { label: 'Duplicate', onClick: () => navigate(`/inspections/create?duplicateFrom=${insp.id}`) },
						canCreate && { label: 'Delete', variant: 'danger', onClick: () => requestDelete(insp.id, insp.buildingName) },
					]}
				/>
			),
		},
	];

	/* ============================================
	 * FILTER CONFIGURATION
	 * ============================================ */

	const filters: FilterControlConfig[] = [
		{ key: 'status', label: 'Inspection Status', type: 'checkbox_group', options: STATUS_FILTER_OPTIONS, value: filterStatuses, onChange: setFilterStatuses },
		{ key: 'rating', label: 'Rating', type: 'checkbox_group', options: RATING_FILTER_OPTIONS, value: filterRatings, onChange: setFilterRatings },
		{ key: 'dateRange', label: 'Date Range', type: 'date_range', value: [filterDateFrom, filterDateTo], onChange: (val: string[]) => { setFilterDateFrom(val[0] || ''); setFilterDateTo(val[1] || ''); } },
		{ key: 'building', label: 'Building', type: 'text', value: filterBuilding, onChange: setFilterBuilding, placeholder: 'Search building name...' },
	];

	/** Resets all filter controls back to their default state — active
	 *  statuses + the 2-week look-back window, with the rest cleared. */
	const resetFilters = () => {
		setFilterStatuses(DEFAULT_STATUS_FILTER);
		setFilterRatings([]);
		setFilterDateFrom(DEFAULT_DATE_FROM);
		setFilterDateTo('');
		setFilterBuilding('');
	};

	/**
	 * Generates and downloads a summary PDF of the currently visible
	 * inspections. Mirrors the WO list export — surfaces the active
	 * filters at the top so a printed copy stays self-explanatory.
	 */
	const handleGeneratePdf = () => {
		setIsGeneratingPdf(true);
		try {
			const filters: ListFilterSummary[] = [
				{ label: 'Status', value: filterStatuses.length > 0 ? filterStatuses.map(formatStatusLabel).join(', ') : '—' },
				{ label: 'Rating', value: filterRatings.length > 0 ? filterRatings.map((r) => RATING_FILTER_OPTIONS.find((o) => o.value === r)?.label ?? r).join(', ') : '—' },
				{ label: 'Date Range', value: filterDateFrom || filterDateTo ? `${filterDateFrom || '—'} – ${filterDateTo || '—'}` : '—' },
				{ label: 'Building', value: filterBuilding || '—' },
			];
			generateInspectionListPdf(inspections, filters);
		} catch (err) {
			alert(err instanceof Error ? err.message : 'Failed to generate PDF');
		} finally {
			setIsGeneratingPdf(false);
		}
	};

	/* ============================================
	 * RENDER -- Delegate to JobListTemplate
	 * ============================================ */

	return (
		<>
			<JobListTemplate
				className="inspections-page"
				dataRef="page-inspections-page"
				title="Inspections"
				subtitle="Quality control and compliance tracking."
				createLabel="New Inspection"
				createRoute="/inspections/create"
				canCreate={canCreate}
				secondaryActionLabel={isGeneratingPdf ? 'Generating…' : 'Generate PDF'}
				onSecondaryActionClick={handleGeneratePdf}
				secondaryActionDisabled={isGeneratingPdf || inspections.length === 0}
				stats={stats}
				columns={columns}
				filters={filters}
				items={inspections}
				isLoading={isLoading}
				error={error}
				currentPage={currentPage}
				totalPages={totalPages}
				totalItems={totalItems}
				perPage={perPage}
				onPageChange={setCurrentPage}
				onRetry={() => query.refetch()}
				onResetFilters={resetFilters}
				emptyMessage={canCreate ? 'No inspections found. Schedule your first inspection to get started.' : 'No inspections found. Check back later for assigned inspections.'}
				/* Surface the Building + Customer pair as a full-width
				   summary band above each row. The columned row below
				   carries the operational fields (ID, Status, Inspector,
				   Assignee, Date, CTAs); the band gives ops the "what
				   building / which customer" context at a glance without
				   spending a column on it. */
				renderRowHeader={(insp: Inspection) => (
					<div className="flex items-center gap-12px flex-wrap">
						<div className="flex items-center gap-6px min-w-0">
							<svg className="w-14px h-14px text-brand-text-muted flex-shrink-0" fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="currentColor" aria-hidden="true">
								<path strokeLinecap="round" strokeLinejoin="round" d="M2.25 21h19.5m-18-18v18m10.5-18v18m6-13.5V21M6.75 6.75h.75m-.75 3h.75m-.75 3h.75m3-6h.75m-.75 3h.75m-.75 3h.75M6.75 21v-3.375c0-.621.504-1.125 1.125-1.125h2.25c.621 0 1.125.504 1.125 1.125V21M3 3h12m-.75 4.5H21m-3.75 3H21m-3.75 3H21" />
							</svg>
							<span className="text-13px font-semibold text-brand-text truncate">{insp.buildingName}</span>
						</div>
						<span className="text-12px text-brand-text-muted">·</span>
						<span className="text-12px text-brand-text-muted truncate">{insp.companyName}</span>
						{/* RecurrenceBadge — same affordance the WO list uses to flag
						    rows that came out of a recurring schedule. */}
						{insp.recurrenceSeriesId && <RecurrenceBadge />}
					</div>
				)}
			/>
			{deleteModal}
		</>
	);
};

export default InspectionsPage;
